// src/components/myAccount/pages/DashboardPage.js
// COMPLETE FILE with CapRaise integration (preserving all existing functionality)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calculator,
  FolderOpen,
  TrendingUp,
  BarChart3,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Crown,
  HelpCircle,
  Users,
  Shield,
  Loader2
} from 'lucide-react';
import subscriptionRestrictionsService from '../../../services/subscriptionRestrictionsService';
import { supabase } from '../../../config';
import SitewideMessage from '../../common/SitewideMessage';

const DashboardPage = ({ user, userProfile, userRoles, onRefreshData }) => {
  const navigate = useNavigate();
  const [userRestrictions, setUserRestrictions] = useState(null);
  const [loadingRestrictions, setLoadingRestrictions] = useState(true);
  const [userSubscription, setUserSubscription] = useState(null);
  const [dealroomViewerCount, setDealroomViewerCount] = useState(0);

  const getUserDisplayName = () => {
    // Use username first (as requested)
    if (userProfile?.username) return userProfile.username;
    // Fallback to email if no username
    if (user?.email) return user.email.split('@')[0];
    return 'User';
  };

  // Get creator products from userRoles
  const getCreatorProducts = () => {
    if (!userRoles || userRoles.length === 0) return [];
    
    const creatorRoles = userRoles.filter(role => role.role === 'creator');
    const productNames = creatorRoles.map(role => {
      const nameMap = {
        'equity_manager': 'Equity',
        'dataroom': 'Dealrooms',
        'capraise': 'CapRaise'
      };
      return nameMap[role.product_name] || role.product_name;
    });
    
    return productNames;
  };

  // Get dealroom viewer info
  const getDealroomViewerStatus = () => {
    if (dealroomViewerCount === 0) return 'No';
    return `Yes (${dealroomViewerCount} Dealroom${dealroomViewerCount > 1 ? 's' : ''})`;
  };

  useEffect(() => {
    const loadSubscription = async () => {
      if (!user?.id) {
        return;
      }

      try {
        const { data, error } = await supabase
          .from('subscriptions')
          .select('plan_type, status, trial_ends_at')
          .eq('user_id', user.id)
          .in('status', ['active', 'trial'])
          .maybeSingle();

        if (!error && data) {
          setUserSubscription(data);
        }
      } catch (error) {
        console.error('Error loading subscription:', error);
      }
    };

    loadSubscription();
  }, [user?.id]);

  // Load dealroom access count (dealrooms where user is creator, viewer, or has any access)
  useEffect(() => {
    const loadDealroomAccess = async () => {
      if (!user?.id) return;

      try {
        // Get all dealrooms where user has access (as creator, viewer, or any role)
        const { data, error } = await supabase
          .from('dataroom_users')
          .select('project_id, role')
          .eq('user_id', user.id)
          .eq('status', 'active');

        if (!error && data) {
          // Count distinct project_ids to avoid counting the same project multiple times
          const uniqueProjectIds = new Set(data.map(item => item.project_id));
          setDealroomViewerCount(uniqueProjectIds.size);
        }
      } catch (error) {
        console.error('Error loading dealroom access:', error);
      }
    };

    loadDealroomAccess();
  }, [user?.id]);

  const hasSubscription = () => {
    // Check both userRoles and database subscription
    const hasRoles = userRoles && userRoles.length > 0;
    const hasDbSubscription = userSubscription && ['active', 'trial'].includes(userSubscription.status);
    return hasRoles || hasDbSubscription;
  };

  const getSubscriptionLevel = () => {
    // Check database subscription first
    if (userSubscription) {
      if (userSubscription.plan_type === 'trial') return 'Trial';
      if (userSubscription.plan_type === 'pro') return 'Pro';
      if (userSubscription.plan_type === 'basic') return 'Basic';
      if (userSubscription.plan_type === 'enterprise') return 'Enterprise';
    }
    
    // Fallback to userRoles check
    const hasProFeatures = userRoles?.some(role => 
      role.role === 'creator' && (role.features?.includes('pro') || role.plan === 'pro' || role.plan_type === 'pro')
    );
    const hasBasicFeatures = userRoles?.some(role => 
      role.plan_type === 'basic' || role.plan === 'basic'
    );
    if (hasProFeatures) return 'Pro';
    if (hasBasicFeatures) return 'Basic';
    return 'Free';
  };

  const isTrialActive = () => {
    if (!userSubscription || userSubscription.status !== 'trial') return false;
    if (!userSubscription.trial_ends_at) return false;
    return new Date(userSubscription.trial_ends_at) > new Date();
  };

  // const getUserPlanType = () => {
  //   // Get plan type from userRoles
  //   const subscriptionRole = userRoles.find(role => role.plan_type || role.plan);
  //   return subscriptionRole?.plan_type || subscriptionRole?.plan || 'free';
  // }; // Unused

  // Load user restrictions
  useEffect(() => {
    const loadRestrictions = async () => {
      if (!user?.id) {
        setLoadingRestrictions(false);
        return;
      }

      try {
        setLoadingRestrictions(true);
        const restrictions = await subscriptionRestrictionsService.getUserRestrictions(user.id);
        setUserRestrictions(restrictions);
      } catch (error) {
        console.error('Error loading restrictions:', error);
      } finally {
        setLoadingRestrictions(false);
      }
    };

    loadRestrictions();
  }, [user?.id, userRoles]);


  // UPDATED handleProductSelect function
  const handleProductSelect = (productId) => {
    if (!hasSubscription() && productId !== 'questions') {
      navigate('/my-account/subscriptions');
      return;
    }

    switch (productId) {
      case 'equityManager':
        navigate('/equity');
        break;
      case 'dataroom':
        navigate('/dealroom');
        break;
      case 'capRaise':  // <- UPDATED: changed from 'capitalRaising' to 'capRaise'
        navigate('/capraise');  // <- UPDATED: changed URL to '/capraise' (no dash)
        break;
      case 'marketData':
        navigate('/market-data');
        break;
      case 'questions':
        navigate('/questions');
        break;
      default:
        console.warn('Unknown product:', productId);
    }
  };

  const subscriptionStatus = hasSubscription() ? 'active' : 'none';

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Zone 2 - Below header (only on dashboard, profile, login-details) */}
      <div className="mb-6">
        <SitewideMessage zone="zone2" userRoles={userRoles} />
      </div>
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {subscriptionStatus === 'none' ? `Welcome, ${getUserDisplayName()}!` : `Welcome back, ${getUserDisplayName()}!`}
        </h1>
        {subscriptionStatus === 'none' && (
          <div className="mt-4 p-6 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Start Your Free 14-Day Trial
                </h2>
                <p className="text-gray-700 mb-4">
                  Get full access to all features for 14 days. No credit card required. Cancel anytime.
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-1 mb-4">
                  <li>Full access to Equity Management</li>
                  <li>Unlimited DealRooms</li>
                  <li>Capital Raising Tools</li>
                  <li>All premium features</li>
                </ul>
              </div>
              <button
                onClick={async () => {
                  try {
                    const response = await fetch('/api/stripe_api/create-trial-subscription', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ user_id: user.id })
                    });
                    const data = await response.json();
                    if (data.success) {
                      if (onRefreshData) onRefreshData();
                      window.location.reload();
                    } else {
                      alert(data.error || 'Failed to start trial');
                    }
                  } catch (error) {
                    console.error('Error starting trial:', error);
                    alert('Failed to start trial. Please try again.');
                  }
                }}
                className="ml-6 bg-green-600 text-white px-8 py-4 rounded-lg hover:bg-green-700 transition-colors flex items-center text-lg font-semibold shadow-lg"
              >
                Start Free Trial
                <ArrowRight className="h-5 w-5 ml-2" />
              </button>
            </div>
          </div>
        )}
        {!subscriptionStatus === 'none' && (
          <p className="text-lg text-gray-600">
            Here's what's happening with your account and business tools.
          </p>
        )}
      </div>

      {/* Subscription Status & Account Snapshot */}
      <div className="mb-8">
        <div className={`rounded-lg border-2 p-6 ${
          subscriptionStatus === 'active' 
            ? 'border-green-200 bg-green-50' 
            : 'border-yellow-200 bg-yellow-50'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {subscriptionStatus === 'active' ? (
                <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
              ) : (
                <AlertCircle className="h-8 w-8 text-yellow-600 mr-3" />
              )}
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {subscriptionStatus === 'active' 
                    ? `${getSubscriptionLevel()} Subscription Active` 
                    : 'No Active Subscription'
                  }
                </h3>
                <p className="text-gray-600">
                  {subscriptionStatus === 'active' 
                    ? 'You have full access to all your subscribed products'
                    : 'Subscribe to a product to get started with your business management tools'
                  }
                </p>
              </div>
            </div>
            {subscriptionStatus === 'none' && (
              <button
                onClick={async () => {
                  // Create trial subscription first
                  try {
                    const response = await fetch('/api/stripe_api/create-trial-subscription', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ user_id: user.id })
                    });
                    const data = await response.json();
                    if (data.success) {
                      // Refresh data and navigate
                      if (onRefreshData) onRefreshData();
                      navigate('/my-account/subscriptions');
                    } else {
                      // If trial creation fails, just navigate to subscriptions
                      navigate('/my-account/subscriptions');
                    }
                  } catch (error) {
                    // If error, just navigate to subscriptions
                    navigate('/my-account/subscriptions');
                  }
                }}
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
              >
                Start Free 14-Day Trial
                <ArrowRight className="h-4 w-4 ml-2" />
              </button>
            )}
            {isTrialActive() && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 mb-3">
                  <strong>Free Trial Active!</strong> Your trial ends in {Math.ceil((new Date(userSubscription.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24))} days.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => navigate('/my-account/subscriptions')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm"
                  >
                    Upgrade to Paid Plan
                  </button>
                  <button
                    onClick={async () => {
                      // Downgrade to free when trial ends
                      if (window.confirm('Are you sure you want to end your trial and switch to the free plan?')) {
                        try {
                          const { error } = await supabase
                            .from('subscriptions')
                            .update({ 
                              plan_type: 'free', 
                              status: 'inactive',
                              trial_ends_at: null 
                            })
                            .eq('user_id', user.id);
                          
                          if (!error && onRefreshData) {
                            onRefreshData();
                            window.location.reload();
                          }
                        } catch (err) {
                          console.error('Error downgrading to free:', err);
                        }
                      }
                    }}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 text-sm"
                  >
                    Switch to Free Plan
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {/* Subscription Level */}
            <div className="rounded-lg bg-white/60 p-4 text-center shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Subscription Level
              </p>
              <p className="mt-2 text-xl font-bold text-blue-600">
                {getSubscriptionLevel()}
              </p>
            </div>
            
            {/* Creator Access */}
            <div className="rounded-lg bg-white/60 p-4 text-center shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Creator Access
              </p>
              <div className="mt-2">
                {getCreatorProducts().length > 0 ? (
                  <p className="text-lg font-bold text-green-600">
                    {getCreatorProducts().join(', ')}
                  </p>
                ) : (
                  <p className="text-lg font-bold text-gray-400">
                    No Access
                  </p>
                )}
              </div>
            </div>
            
            {/* Dealroom Access */}
            <div className="rounded-lg bg-white/60 p-4 text-center shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Dealroom Access
              </p>
              <p className={`mt-2 text-xl font-bold ${dealroomViewerCount > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                {getDealroomViewerStatus()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Plan Limits & Restrictions */}
      {userRestrictions && !loadingRestrictions && (
        <div className="mb-8 bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Shield className="h-6 w-6 text-blue-600 mr-3" />
              <h2 className="text-2xl font-semibold text-gray-900">Your Plan Limits</h2>
            </div>
            <button
              onClick={() => navigate('/my-account/subscriptions')}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
            >
              Upgrade Plan
              <ArrowRight className="h-4 w-4 ml-1" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Equity */}
            {userRestrictions.restrictions.equity && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Calculator className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="font-semibold text-gray-900">Equity</h3>
                </div>
                {userRestrictions.restrictions.equity.enabled ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Max Companies:</span>
                      <span className="font-medium">{userRestrictions.restrictions.equity.max_companies === -1 ? 'Unlimited' : userRestrictions.restrictions.equity.max_companies}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Max Shareholders:</span>
                      <span className="font-medium">{userRestrictions.restrictions.equity.max_shareholders === -1 ? 'Unlimited' : userRestrictions.restrictions.equity.max_shareholders}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Not included in your plan</p>
                )}
              </div>
            )}

            {/* Dataroom */}
            {userRestrictions.restrictions.dataroom && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <FolderOpen className="h-5 w-5 text-green-600 mr-2" />
                  <h3 className="font-semibold text-gray-900">Dataroom</h3>
                </div>
                {userRestrictions.restrictions.dataroom.enabled ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Max Projects:</span>
                      <span className="font-medium">{userRestrictions.restrictions.dataroom.max_projects === -1 ? 'Unlimited' : userRestrictions.restrictions.dataroom.max_projects}</span>
                    </div>
                    {userRestrictions.restrictions.dataroom.max_items_per_project !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Items/Project:</span>
                        <span className="font-medium">{userRestrictions.restrictions.dataroom.max_items_per_project === -1 ? 'Unlimited' : userRestrictions.restrictions.dataroom.max_items_per_project}</span>
                      </div>
                    )}
                    {userRestrictions.restrictions.dataroom.max_data_gb !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Storage:</span>
                        <span className="font-medium">{userRestrictions.restrictions.dataroom.max_data_gb === -1 ? 'Unlimited' : `${userRestrictions.restrictions.dataroom.max_data_gb} GB`}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Not included in your plan</p>
                )}
              </div>
            )}

            {/* Questions */}
            {userRestrictions.restrictions.questions && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <HelpCircle className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="font-semibold text-gray-900">Questions</h3>
                </div>
                {userRestrictions.restrictions.questions.enabled ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Max Questions:</span>
                      <span className="font-medium">{userRestrictions.restrictions.questions.max_questions === -1 ? 'Unlimited' : userRestrictions.restrictions.questions.max_questions}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Not included in your plan</p>
                )}
              </div>
            )}

            {/* CapRaise */}
            {userRestrictions.restrictions.capraise && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <TrendingUp className="h-5 w-5 text-purple-600 mr-2" />
                  <h3 className="font-semibold text-gray-900">CapRaise</h3>
                </div>
                {userRestrictions.restrictions.capraise.enabled ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Max Deals:</span>
                      <span className="font-medium">{userRestrictions.restrictions.capraise.max_deals === -1 ? 'Unlimited' : userRestrictions.restrictions.capraise.max_deals}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Not included in your plan</p>
                )}
              </div>
            )}

            {/* Investor CRM */}
            {userRestrictions.restrictions.investor_crm && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Users className="h-5 w-5 text-purple-600 mr-2" />
                  <h3 className="font-semibold text-gray-900">Investor CRM</h3>
                </div>
                {userRestrictions.restrictions.investor_crm.enabled ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Max Contacts:</span>
                      <span className="font-medium">{userRestrictions.restrictions.investor_crm.max_contacts === -1 ? 'Unlimited' : userRestrictions.restrictions.investor_crm.max_contacts}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Not included in your plan</p>
                )}
              </div>
            )}

            {/* Analysis */}
            {userRestrictions.restrictions.analysis && (
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <BarChart3 className="h-5 w-5 text-indigo-600 mr-2" />
                  <h3 className="font-semibold text-gray-900">Analysis</h3>
                </div>
                {userRestrictions.restrictions.analysis.enabled ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Data Access:</span>
                      <span className="font-medium capitalize">{userRestrictions.restrictions.analysis.data_access || 'Limited'}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Not included in your plan</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {loadingRestrictions && (
        <div className="mb-8 bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600 mr-3" />
            <span className="text-gray-600">Loading plan limits...</span>
          </div>
        </div>
      )}

      {/* Product Access Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Products</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Equity */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow flex flex-col">
            <div className="flex items-center mb-4">
              <Calculator className="h-8 w-8 text-blue-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Equity</h3>
            </div>
            <p className="text-gray-600 mb-4 flex-grow">
              Manage shareholders, directors, and share issuances for your companies.
            </p>
            <div className="flex items-center justify-between mb-4">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                hasSubscription() 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {hasSubscription() ? 'Active' : 'Requires Subscription'}
              </span>
            </div>
            <button
              onClick={() => handleProductSelect('equityManager')}
              className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                hasSubscription()
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              {hasSubscription() ? 'Launch' : 'Subscribe'}
            </button>
          </div>

          {/* Dataroom */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow flex flex-col">
            <div className="flex items-center mb-4">
              <FolderOpen className="h-8 w-8 text-green-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Dataroom</h3>
            </div>
            <p className="text-gray-600 mb-4 flex-grow">
              Secure document sharing for due diligence, board meetings, and reporting.
            </p>
            <div className="flex items-center justify-between mb-4">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                hasSubscription() 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {hasSubscription() ? 'Active' : 'Requires Subscription'}
              </span>
            </div>
            <button
              onClick={() => handleProductSelect('dataroom')}
              className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                hasSubscription()
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              {hasSubscription() ? 'Launch' : 'Subscribe'}
            </button>
          </div>

          {/* CapRaise - UPDATED */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow flex flex-col">
            <div className="flex items-center mb-4">
              <TrendingUp className="h-8 w-8 text-purple-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">CapRaise</h3>
            </div>
            <p className="text-gray-600 mb-4 flex-grow">
              Professional capital raising platform with investor CRM, deal rooms, and termsheet generation.
            </p>
            <div className="flex items-center justify-between mb-4">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                hasSubscription() 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {hasSubscription() ? 'Active' : 'Requires Subscription'}
              </span>
            </div>
            <button
              onClick={() => handleProductSelect('capRaise')}
              className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                hasSubscription()
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              {hasSubscription() ? 'Launch' : 'Subscribe'}
            </button>
          </div>

          {/* MarketData */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow flex flex-col">
            <div className="flex items-center mb-4">
              <BarChart3 className="h-8 w-8 text-indigo-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">MarketData</h3>
            </div>
            <p className="text-gray-600 mb-4 flex-grow">
              Explore macro data, crypto dashboards, portfolio tools, and private company filings.
            </p>
            <div className="flex items-center justify-between mb-4">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                hasSubscription()
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {hasSubscription() ? 'Active' : 'Requires Subscription'}
              </span>
            </div>
            <button
              onClick={() => handleProductSelect('marketData')}
              className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                hasSubscription()
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              {hasSubscription() ? 'Launch' : 'Subscribe'}
            </button>
          </div>

        </div>
      </div>

      {/* Quick Actions Section (for subscribed users) */}
      {hasSubscription() && (
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/my-account/subscriptions')}
              className="bg-white border border-gray-200 rounded-lg p-4 text-left hover:shadow-md transition-shadow"
            >
              <div className="flex items-center">
                <Crown className="h-6 w-6 text-yellow-600 mr-3" />
                <div>
                  <h3 className="font-medium text-gray-900">Manage Subscription</h3>
                  <p className="text-sm text-gray-600">View billing and upgrade options</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate('/my-account/profile')}
              className="bg-white border border-gray-200 rounded-lg p-4 text-left hover:shadow-md transition-shadow"
            >
              <div className="flex items-center">
                <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
                <div>
                  <h3 className="font-medium text-gray-900">Update Profile</h3>
                  <p className="text-sm text-gray-600">Keep your information current</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate('/questions')}
              className="bg-white border border-gray-200 rounded-lg p-4 text-left hover:shadow-md transition-shadow"
            >
              <div className="flex items-center">
                <HelpCircle className="h-6 w-6 text-blue-600 mr-3" />
                <div>
                  <h3 className="font-medium text-gray-900">Get Support</h3>
                  <p className="text-sm text-gray-600">Ask questions or get help</p>
                </div>
              </div>
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default DashboardPage;